import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import { User } from 'src/interfaces/iuser';
import { getTokenThunk } from 'src/store/Auth/actions';
import { validateEmail } from 'src/utils/functions';

export const useEmailValidation = (debounceDelay: number = 200) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const emailInput = event.target.value.trim().toLowerCase();
      setEmail(emailInput);

      // cleanups/re-initializations
      setEmailError(false);
      setEmailErrorMessage('');
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Set new debounce timeout
      const newDebounceTimeout = setTimeout(() => {
        if (!validateEmail(emailInput)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
      }, debounceDelay);

      setDebounceTimeout(newDebounceTimeout);
    },
    [debounceTimeout, debounceDelay]
  );

  // Cleanup the timeout when component unmounts or on new renders
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  return { email, emailError, emailErrorMessage, handleEmailChange };
};

export const useIsLoggedIn = () => {
  const dispatch = useDispatch();
  const isLoggedIn = Boolean(dispatch(getTokenThunk()));
  return isLoggedIn;
};

export const useSubscription = (isLoggedIn, setLoginModalOpen) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [requiredSubscriptionPlan, setRequiredSubscriptionPlan] = useState<SubscriptionPlan>('pro');
  const [subscriptionModalView, setSubscriptionModalView] = useState<'basic' | 'detailed'>(
    'detailed'
  );
  const [
    subscriptionModalOrigin,
    setSubscriptionModalOrigin,
  ] = useState<SubscriptionModalOrigin | null>(null);
  const [showUpgradeSubscriptionModal, setShowUpgradeSubscriptionModal] = useState<
    null | 'expert' | 'scale'
  >(null);
  const [showSearchCreditDeductWarningModal, setShowSearchCreditDeductWarningModal] = useState(
    false
  );

  useEffect(() => {
    // cleanup
    if (!subscriptionModalOpen) {
      setSubscriptionModalView('detailed');
    }
  }, [subscriptionModalOpen]);

  const handleOnSampleClick = (
    reqSubscriptionPlan: SubscriptionPlan = 'pro',
    origin: SubscriptionModalOrigin | null = null
  ) => {
    const userSubscriptionPlan = user.subscriptionPlan;

    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }

    const page = window.location.pathname.split('/')[1];
    if (page === 'markets') {
      setSubscriptionModalOpen(true);
      return;
    }

    // Allow 'expert' users access to all 'pro' requirements
    // Allow 'scale' users access to all 'pro' and 'expert' requirements
    const hasAccess =
      (reqSubscriptionPlan === 'pro' && ['expert', 'scale'].includes(userSubscriptionPlan)) ||
      (reqSubscriptionPlan === 'expert' && userSubscriptionPlan === 'scale') ||
      reqSubscriptionPlan === userSubscriptionPlan;

    if (hasAccess) {
      setShowSearchCreditDeductWarningModal(true);
    } else {
      setSubscriptionModalView('basic');
      setRequiredSubscriptionPlan(reqSubscriptionPlan);
      setSubscriptionModalOrigin(origin);
      if (userSubscriptionPlan == 'starter') {
        setSubscriptionModalOpen(true);
      } else {
        reqSubscriptionPlan !== 'pro'
          ? setShowUpgradeSubscriptionModal(reqSubscriptionPlan as 'expert' | 'scale')
          : setSubscriptionModalOpen(true);
      }
    }
  };

  return {
    subscriptionModalOpen,
    setSubscriptionModalOpen,
    requiredSubscriptionPlan,
    setRequiredSubscriptionPlan,
    subscriptionModalView,
    setSubscriptionModalView,
    subscriptionModalOrigin,
    showUpgradeSubscriptionModal,
    setShowUpgradeSubscriptionModal,
    showSearchCreditDeductWarningModal,
    setShowSearchCreditDeductWarningModal,
    handleOnSampleClick,
  };
};
